import React, { useState } from 'react'
import styled from 'styled-components'
import * as Yup from 'yup'
import { Answer } from 'clients/quiz-service'
import { FormScreen, FormValues } from 'components/screen-templates/FormScreen'
import { onBackClickCallback, onNextClickType } from 'components/types'
import { HeightUnit } from 'libs/weight/types'
import { Quizzes } from 'redux/quiz/types'
import { GifName } from '../../data/gifs'
import { DSFlex, DSInput, DSSpacer, DSText } from '@zoe/ds-web'
import { FieldError } from '../core/fields/FieldError'
import { useFormContext, useController } from 'react-hook-form'
import { getFormattedHeightValues } from '../../libs/height/height-helpers'
import { ZiggieAsk } from '../core/ziggie/ZiggieAsk'

const HEIGHT_UNIT_OPTIONS = [
  { id: 'ft', label: 'ft' },
  { id: 'cm', label: 'cm' },
]

const SegmentedControl = styled.div`
  display: flex;
  border-radius: var(--radius-8, 8px);
  overflow: hidden;
  border: 1px solid var(--colour-border-primary, #e2e2e2);
  width: fit-content;
`

const SegmentButton = styled.button<{ $active: boolean }>`
  padding: 0 var(--grid-24);
  height: 36px;
  border: none;
  background: ${({ $active }) => ($active ? 'var(--colour-text-primary, #2d3246)' : 'white')};
  color: ${({ $active }) => ($active ? 'white' : 'var(--colour-text-primary, #2d3246)')};
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;

  &:not(:last-child) {
    border-right: 1px solid var(--colour-border-primary, #e2e2e2);
  }
`

export type InputHeightScreenProps = {
  title: string
  defaultUnit: HeightUnit
  subtitle?: string
  topImage?: string
  topImageAlt?: string
  gif?: GifName
  buttonText?: string
  // Ziggie asks the question from a speech bubble, in place of the title and subtitle.
  withZiggie?: { gif: GifName; typewriter?: boolean }
}

type Props = InputHeightScreenProps & {
  onBackClick?: onBackClickCallback
  onNextClick: onNextClickType
  quiz: Quizzes
}

export const InputHeightScreen: React.FC<Props> = ({
  title,
  subtitle,
  defaultUnit,
  topImage,
  topImageAlt,
  gif,
  buttonText,
  withZiggie,
  onNextClick,
  onBackClick,
  quiz,
}) => {
  const [selectedUnit, setSelectedUnit] = useState<string>((quiz.height_unit as string) || defaultUnit)

  const handleOnNextClick = (values?: FormValues | Answer[] | any) => {
    onNextClick(getFormattedHeightValues(values))
  }

  return (
    <FormScreen
      topImage={topImage}
      topImageAlt={topImageAlt}
      gif={gif}
      screenTitle={title}
      description={subtitle}
      buttonText={buttonText ?? 'Continue'}
      showHomepageLink={false}
      questionSlot={
        withZiggie && <ZiggieAsk gif={withZiggie.gif} text={title} quiz={quiz} typewriter={withZiggie.typewriter} />
      }
      form={{
        body: <InputHeightScreenBody selectedUnit={selectedUnit} onSelect={setSelectedUnit} />,
        validationSchema: validationSchema(),
        // needed to prevent React "uncontrolled" error showing
        initialValues: {
          height_ft: String(quiz.height_ft ?? ''),
          height_in: String(quiz.height_in ?? ''),
          height_cm: String(quiz.height_cm ?? ''),
          height_unit: quiz.height_unit || defaultUnit,
        },
      }}
      onNextClick={handleOnNextClick}
      onBackClick={onBackClick}
    />
  )
}

export const InputHeightScreenBody = ({
  selectedUnit,
  onSelect,
  label,
}: {
  selectedUnit: string
  onSelect: (value: ((prevState: string) => string) | string) => void
  label?: string
}): JSX.Element => {
  const {
    formState: { errors, touchedFields },
    setValue,
    clearErrors,
  } = useFormContext<any>()

  const selectedError =
    selectedUnit === 'ft'
      ? (touchedFields['height_ft'] && errors['height_ft']?.message) ||
        (touchedFields['height_in'] && errors['height_in']?.message)
      : touchedFields['height_cm'] && errors['height_cm']?.message

  const clearFieldAndErrors = async (fieldName: string) => {
    setValue(fieldName, undefined)
    clearErrors(fieldName)
  }

  const resetForm = async () => {
    await clearFieldAndErrors('height_ft')
    await clearFieldAndErrors('height_in')
    await clearFieldAndErrors('height_cm')
  }

  const onSelectWithReset = (option: string) => {
    resetForm().then(() => {
      onSelect(option)
      setValue('height_unit', option, { shouldDirty: true, shouldValidate: true })
    })
  }

  const heightFt = useController({ name: 'height_ft' })
  const heightIn = useController({ name: 'height_in' })
  const heightCm = useController({ name: 'height_cm' })

  return (
    <div style={{ width: '100%' }}>
      <SegmentedControl>
        {HEIGHT_UNIT_OPTIONS.map((opt) => (
          <SegmentButton
            key={opt.id}
            $active={selectedUnit === opt.id}
            onClick={() => onSelectWithReset(opt.id)}
            type="button"
          >
            <DSText variant="fluid-paragraph-200" color={selectedUnit === opt.id ? 'primary-inverse' : 'primary'}>
              {opt.label}
            </DSText>
          </SegmentButton>
        ))}
      </SegmentedControl>
      <DSSpacer size={16} direction="vertical" />
      <div>
        {selectedUnit === 'ft' ? (
          <DSFlex direction="row">
            <DSInput
              id="height-ft"
              label={label ?? 'Height'}
              name={heightFt.field.name}
              value={heightFt.field.value ?? ''}
              onChange={heightFt.field.onChange}
              onBlur={heightFt.field.onBlur}
              placeholder=""
              suffix="ft"
              type="number"
              testId="height-ft-input"
            />
            <DSSpacer size={12} direction="horizontal" />
            <DSInput
              id="height-in"
              label={'\u00A0'}
              name={heightIn.field.name}
              value={heightIn.field.value ?? ''}
              onChange={heightIn.field.onChange}
              onBlur={heightIn.field.onBlur}
              placeholder=""
              suffix="in"
              type="number"
              testId="height-in-input"
            />
          </DSFlex>
        ) : (
          <DSInput
            id="height-cm"
            label={label ?? 'Height'}
            name={heightCm.field.name}
            value={heightCm.field.value ?? ''}
            onChange={heightCm.field.onChange}
            onBlur={heightCm.field.onBlur}
            placeholder=""
            suffix="cm"
            type="number"
            testId="height-cm-input"
          />
        )}
      </div>
      {selectedError && <FieldError>{selectedError}</FieldError>}
    </div>
  )
}

const GENERIC_VALUE_ERROR = 'Please enter a value to continue'

export const heightValidationSchema = {
  height_unit: Yup.string().required(),
  height_ft: Yup.number().when('height_unit', {
    is: (height_unit: string) => height_unit === 'ft',
    then: () => Yup.number().typeError(GENERIC_VALUE_ERROR).required(GENERIC_VALUE_ERROR).integer(GENERIC_VALUE_ERROR),
    otherwise: () => Yup.mixed().notRequired(),
  }),
  height_in: Yup.number().when('height_unit', {
    is: (height_unit: string) => height_unit === 'ft',
    then: () =>
      Yup.number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .notRequired()
        .typeError(GENERIC_VALUE_ERROR)
        .integer(GENERIC_VALUE_ERROR),
    otherwise: () => Yup.mixed().notRequired(),
  }),
  height_cm: Yup.number().when('height_unit', {
    is: (height_unit: string) => height_unit === 'cm',
    then: () => Yup.number().typeError(GENERIC_VALUE_ERROR).required(GENERIC_VALUE_ERROR).integer(GENERIC_VALUE_ERROR),
    otherwise: () => Yup.mixed().notRequired(),
  }),
}
export const validationSchema = (): Yup.AnyObjectSchema => Yup.object(heightValidationSchema)
